import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { NewsletterSubscribeSection } from "src/components/newsletter-subscribe-section";
import { RouterLink } from "src/components/router-link";
import { paths } from "src/paths";
import { hostedNightscoutPlans } from "src/sections/pricing/hosted-nightscout-plans";
import {
  HostedNightscoutBillingCycleRadios,
  type HostedNightscoutBillingCycle,
} from "src/sections/pricing/hosted-nightscout-billing";
import {
  ServicePlanCard,
  getServicePlanIllustration,
  type ServicePlanCardData,
} from "src/sections/pricing/service-plan-card";

interface HomeFeaturesProps {
  targetRef?: React.RefObject<HTMLDivElement>;
  /** When false, hide the newsletter block (homepage shows it once before FAQ). Default true for `/pricing` only. */
  showNewsletter?: boolean;
}

export const HomeFeatures: FC<HomeFeaturesProps> = ({
  targetRef,
  showNewsletter = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [billingCycle, setBillingCycle] =
    useState<HostedNightscoutBillingCycle>("monthly");

  const nightscoutPlan = hostedNightscoutPlans.find(
    (plan) => plan.id === "technology"
  );

  const technicalCoaching: ServicePlanCardData = {
    id: "onboarding",
    category: "Coaching",
    title: "Technical guidance",
    description: "Less stress about the technical setup and configuration",
    price: "",
    details: [
      "Onboarding session",
      "Device setup and configuration",
      "Help with technical errors and questions",
      "24/7 WhatsApp Helpdesk",
      "Free intake session",
    ],
    buttonText: "Book now",
    buttonVariant: "outlined",
    illustrationBg: "#FCE7F3",
    link: "https://cal.com/team/opensourcelinic/onboarding",
  };

  const medicalCoaching: ServicePlanCardData = {
    id: "coaching",
    category: "Coaching",
    title: "Medical guidance",
    description: "So you can focus on living your best life",
    price: "",
    details: [
      "Guided basal, ISF/dyn ISF, and CR configuration",
      "Help with medical questions",
      "24/7 WhatsApp Helpdesk",
      "Free intake session",
    ],
    buttonText: "Book now",
    buttonVariant: "outlined",
    illustrationBg: "#FEF3C7",
    link: "https://cal.com/team/opensourcelinic/medical-coaching",
    badge: "medical",
  };

  const journeyPlans: ServicePlanCardData[] = [
    ...(nightscoutPlan ? [nightscoutPlan] : []),
    technicalCoaching,
    medicalCoaching,
  ];

  const otherCoachingOptions = [
    { id: "carbless", title: "CarblessLooping" },
    { id: "home-visit", title: "Home visit" },
    { id: "loop-config-short", title: "Loop configuration short" },
    { id: "loop-config-long", title: "Loop configuration long" },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "grey.50",
        py: "120px",
      }}
      ref={targetRef}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            component="h2"
            variant="h2"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: "bold",
              mb: 2,
              fontSize: smDown ? "2rem" : "3rem",
              lineHeight: 1.2,
            }}
          >
            {t("Start your journey")}
          </Typography>
        </Box>

        <HostedNightscoutBillingCycleRadios
          value={billingCycle}
          onChange={setBillingCycle}
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            mb: 4,
          }}
        />
        <Grid container spacing={{ xs: 3, md: 2 }}>
          {journeyPlans.map((service) => (
            <Grid xs={12} md={4} key={service.id}>
              <ServicePlanCard
                service={service}
                billingCycle={billingCycle}
                illustration={getServicePlanIllustration(service.id)}
              />
            </Grid>
          ))}
        </Grid>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            mt: 5,
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t("See other options:")}
          </Typography>
          {otherCoachingOptions.map((option) => (
            <Button
              key={option.id}
              component={RouterLink}
              href={`${paths.medicalCoaching}#${option.id}`}
              variant="text"
              sx={{
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t(option.title)}
            </Button>
          ))}
        </Stack>
      </Container>
      <NewsletterSubscribeSection show={showNewsletter} />
    </Box>
  );
};

export default HomeFeatures;
